import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageBody, SectionLabel, Spinner, backLinkVariants } from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { CohortFilter } from '@/components/pickers/CohortFilter.tsx';
import { CategoryCard } from '@/components/dashboard/CategoryCard.tsx';
import { TypicalQuestionsCard } from '@/components/dashboard/TypicalQuestionsCard.tsx';
import { sortCategories } from '@/components/dashboard/questionCategories.ts';
import {
  fetchQuestionDescriptions,
  fetchQuestionsOverview,
  fetchTypicalQuestions,
  type DashboardScope,
} from '@/lib/dashboard.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { useCohortInOrganisation } from '@/lib/useCohortInOrganisation.ts';
import type { QuestionCategory } from '@/types/messages.ts';

export interface QuestionsSearch {
  /** Which cohort the breakdown is about. Absent is the whole organisation. */
  cohort: string | undefined;
}

export const Route = createFileRoute('/_app/dashboard/questions')({
  staticData: { orgScope: 'required' },
  validateSearch: (search: Record<string, unknown>): QuestionsSearch => ({
    cohort:
      typeof search.cohort === 'string' && search.cohort !== ''
        ? search.cohort
        : undefined,
  }),
  component: QuestionsOverviewRoute,
});

function QuestionsOverviewRoute() {
  const { cohort } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <QuestionsOverviewPage
      cohort={cohort}
      onSelectCohort={(next) =>
        void navigate({ to: '/dashboard/questions', search: { cohort: next } })
      }
    />
  );
}

export interface QuestionsOverviewPageProps {
  cohort: string | undefined;
  onSelectCohort: (cohort: string | undefined) => void;
}

export function QuestionsOverviewPage({
  cohort: asked,
  onSelectCohort,
}: QuestionsOverviewPageProps) {
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id;
  const { cohort, settled } = useCohortInOrganisation(asked, organisationId);

  const scope: DashboardScope = { cohortId: cohort, organisationId };
  const scopeKey = [cohort ?? null, organisationId ?? null];

  const overview = useQuery({
    queryKey: ['questionsOverview', ...scopeKey],
    queryFn: () => fetchQuestionsOverview(scope),
    enabled: settled,
    throwOnError: true,
  });

  /*
   * Both of these are model calls behind the same filter, and both are slower
   * than the counts. They run beside the overview rather than after it, exactly
   * as the Vue view fired them off without awaiting either.
   */
  const descriptions = useQuery({
    queryKey: ['questionDescriptions', ...scopeKey],
    queryFn: () => fetchQuestionDescriptions(scope),
    enabled: settled,
    throwOnError: true,
  });

  const insights = useQuery({
    queryKey: ['typicalQuestions', ...scopeKey],
    queryFn: () => fetchTypicalQuestions(scope),
    enabled: settled,
    throwOnError: true,
  });

  const descriptionFor = (category: QuestionCategory): string | null =>
    descriptions.data?.descriptions.find((one) => one.category === category)
      ?.description ?? null;

  const describes = (category: QuestionCategory): boolean =>
    descriptions.data?.descriptions.some((one) => one.category === category) ?? false;

  const categories = sortCategories(overview.data?.data.categories ?? []);

  return (
    <>
      <Link
        to='/dashboard'
        search={{ cohort }}
        className={backLinkVariants({ className: 'mb-2.5' })}
      >
        Dashboard
      </Link>

      <PageHeader
        title='Questions Overview'
        subtitle='Aggregated insights into leadership questions'
        filters={
          organisationId && (
            <CohortFilter
              value={cohort}
              onChange={onSelectCohort}
              organisationId={organisationId}
            />
          )
        }
      />

      <PageBody>
        {overview.data === undefined ? (
          <p className='flex items-center gap-2 text-sm text-ink-muted'>
            <Spinner />
            Loading questions…
          </p>
        ) : (
          <div className='flex flex-col gap-6'>
            <TypicalQuestionsCard insights={insights.data} loading={insights.isPending} />

            <SectionLabel as='h2'>Questions per Leadership Domain</SectionLabel>

            <ul className='grid grid-cols-3 gap-4 max-agenda:grid-cols-2 max-mobile:grid-cols-1'>
              {categories.map((category) => (
                <CategoryCard
                  key={category.category}
                  category={category}
                  description={descriptionFor(category.category)}
                  loading={descriptions.isPending && !describes(category.category)}
                />
              ))}
            </ul>

            <div className='flex items-center justify-between border-t border-line pt-4 text-sm'>
              <span className='text-ink-muted'>Total questions analyzed</span>
              <span className='font-semibold text-ink tabular-nums'>
                {overview.data.data.total_questions}
              </span>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
